/** PROTOTYPE variant C — expandable side-by-side diff per row. */

import { useState } from "react";
import {
    ACTION_LABEL,
    AmountCell,
    MatchBadge,
    getExtractedLabel,
    getNewAmount,
    getRowStatus,
    showCreateButton,
    showIgnoreButton,
    showKeepButton,
    showUpdateButton,
} from "./hrpReconciliationPrototypeShared";

const DiffPanel = ({ row }) => {
    const extracted = getExtractedLabel(row);
    const hrpAmount = row.hrpCurrentAmount;
    const newAmount = getNewAmount(row);
    const amountChanged = hrpAmount != null && newAmount != null && hrpAmount !== newAmount;
    const nameChanged = row.nameMismatch;

    return (
        <div className="billing-rate-builder__prototype-diff">
            <div className="billing-rate-builder__prototype-diff-col">
                <span className="billing-rate-builder__prototype-diff-label">In HRP</span>
                <strong>{row.hrpComponentName ?? "—"}</strong>
                <AmountCell amount={hrpAmount} feeType={row.extractedFeeType} />
                {row.hrpBillingMethod && (
                    <div className="billing-rate-builder__muted">{row.hrpBillingMethod}</div>
                )}
            </div>
            <div className="billing-rate-builder__prototype-diff-col">
                <span className="billing-rate-builder__prototype-diff-label">Extracted</span>
                <strong className={nameChanged ? "billing-rate-builder__prototype-diff-changed" : undefined}>
                    {extracted ?? "—"}
                </strong>
                <AmountCell amount={row.extractedAmount} feeType={row.extractedFeeType} />
            </div>
            <div className="billing-rate-builder__prototype-diff-col">
                <span className="billing-rate-builder__prototype-diff-label">New (target)</span>
                <strong className={amountChanged ? "billing-rate-builder__prototype-diff-changed" : undefined}>
                    {extracted ?? row.hrpComponentName ?? "—"}
                </strong>
                <AmountCell amount={newAmount} feeType={row.extractedFeeType} />
            </div>
        </div>
    );
};

const HrpReconciliationVariantC = ({ rows, hrpSnapshot, onUpdate, onKeep, onIgnore, onCreate }) => {
    const [expanded, setExpanded] = useState(() => new Set(["extracted-0"]));
    const pending = rows.filter((r) => !r.confirmed && !r.skipped);
    const asOfLabel = hrpSnapshot?.asOfDate ? ` as of ${hrpSnapshot.asOfDate}` : "";

    const toggle = (rowId) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    };

    return (
        <div className="billing-rate-builder__alert billing-rate-builder__hrp-reconciliation">
            <div className="billing-rate-builder__page-header">
                <div>
                    <p className="title billing-rate-builder__alert-title">HRP reconciliation{asOfLabel}</p>
                    <p className="billing-rate-builder__alert-body-plain">
                        Expand each row to compare HRP, extracted, and target values side by side.
                        {pending.length > 0 ? ` ${pending.length} pending.` : " All rows resolved."}
                    </p>
                </div>
            </div>

            <div className="billing-rate-builder__prototype-expand-list">
                {rows.map((row) => {
                    const isOpen = expanded.has(row.rowId);
                    const isPending = !row.confirmed && !row.skipped;
                    const action = row.userAction ?? row.suggestedAction;

                    return (
                        <div
                            key={row.rowId}
                            className={[
                                "billing-rate-builder__prototype-expand-item",
                                row.nameMismatch && isPending ? "billing-rate-builder__prototype-row--mismatch" : "",
                                row.skipped ? "billing-rate-builder__prototype-row--ignored" : "",
                                row.hrpKeepApplied ? "billing-rate-builder__prototype-row--kept" : "",
                            ].filter(Boolean).join(" ")}
                        >
                            <button
                                type="button"
                                className="billing-rate-builder__prototype-expand-trigger"
                                onClick={() => toggle(row.rowId)}
                                aria-expanded={isOpen}
                            >
                                <span className="billing-rate-builder__prototype-expand-chevron">{isOpen ? "▾" : "▸"}</span>
                                <MatchBadge matchType={row.matchType} />
                                <span className="billing-rate-builder__prototype-expand-title">
                                    {row.hrpComponentName ?? getExtractedLabel(row) ?? row.rowId}
                                </span>
                                <span className="billing-rate-builder__prototype-expand-status">{getRowStatus(row)}</span>
                                {row.nameMismatch && isPending && (
                                    <span className="billing-rate-builder__prototype-warning-pill">Name mismatch</span>
                                )}
                            </button>

                            {isOpen && (
                                <div className="billing-rate-builder__prototype-expand-body">
                                    <DiffPanel row={row} />
                                    {isPending && (
                                        <div className="billing-rate-builder__hrp-reconciliation-actions billing-rate-builder__prototype-expand-actions">
                                            {showUpdateButton(row) && (
                                                <button type="button" className="btn edit-btn" onClick={() => onUpdate(row.rowId)}>
                                                    Update — apply {ACTION_LABEL.amend.toLowerCase()} values
                                                </button>
                                            )}
                                            {showKeepButton(row) && (
                                                <button
                                                    type="button"
                                                    className="btn billing-rate-builder__prototype-btn-keep"
                                                    onClick={() => onKeep(row.rowId)}
                                                >
                                                    Keep HRP — no document changes
                                                </button>
                                            )}
                                            {showCreateButton(row) && (
                                                <button type="button" className="btn edit-btn" onClick={() => onCreate(row.rowId)}>
                                                    Create new component
                                                </button>
                                            )}
                                            {showIgnoreButton(row) && (
                                                <button type="button" className="btn" onClick={() => onIgnore(row.rowId)}>
                                                    Ignore row
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {!isPending && (
                                        <p className="billing-rate-builder__muted billing-rate-builder__prototype-resolved-note">
                                            Resolved as <strong>{ACTION_LABEL[action] ?? action}</strong>
                                            {row.hrpKeepApplied && " — extracted column now shows HRP name"}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default HrpReconciliationVariantC;
